import { isWeb, type TransitionProp } from "tamagui";

// Variants cannot carry `transition`; components attach the spring themselves.
export type Press = "default" | "strong" | "none";
export type Appear = "fade" | "rise" | "scale" | "drop" | "statement";

export const pressVariants = {
  default: { pressStyle: { scale: 0.97 } },
  strong: { pressStyle: { scale: 0.94 } },
  none: {},
} as const satisfies Record<Press, object>;

// Web wants CSS property names; native wants shorthands expanded.
const nativeExpansions: Record<string, string[]> = {
  borderColor: [
    "borderTopColor",
    "borderRightColor",
    "borderBottomColor",
    "borderLeftColor",
  ],
};
export const animateOnly = (...keys: string[]) =>
  isWeb
    ? keys.map(key => key.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`))
    : keys.flatMap(key => nativeExpansions[key] ?? [key]);

// `opacity: 1` pins the exit start; see the opacity gotcha in references/tamagui.md.
export const appearVariants = {
  fade: { opacity: 1, enterStyle: { opacity: 0 }, exitStyle: { opacity: 0 } },
  rise: {
    opacity: 1,
    enterStyle: { opacity: 0, y: 8 },
    exitStyle: { opacity: 0, y: 8 },
  },
  scale: {
    opacity: 1,
    enterStyle: { opacity: 0, scale: 0.96 },
    exitStyle: { opacity: 0, scale: 0.96 },
  },
  drop: {
    opacity: 1,
    enterStyle: { opacity: 0, y: -8, scale: 0.96 },
    exitStyle: { opacity: 0, y: -8, scale: 0.96 },
  },
  statement: {
    opacity: 1,
    enterStyle: { opacity: 0, y: 16, scale: 0.9 },
    exitStyle: { opacity: 0, y: 8 },
  },
} as const satisfies Record<Appear, object>;

// Native needs a resting key or it freezes at enterStyle; web must not have
// one or every at-rest style change springs.
const restingKey = isWeb ? {} : { default: "enter" as const };
export const presenceTransition = {
  ...restingKey,
  enter: "enter",
  exit: "exit",
  opacity: "fade",
} as const;

type TransitionObject = Exclude<TransitionProp, string | readonly unknown[]>;

const appearTransition = (
  shape: Appear,
  pressing: boolean,
): TransitionObject => {
  const enter = shape === "statement" ? "expressive" : "enter";
  return {
    ...presenceTransition,
    ...(pressing ? { default: "press" } : isWeb ? {} : { default: enter }),
    enter,
  };
};

// Spread, not a prop: `transition={undefined}` still counts as animated.
export const motionTransition = (
  press: Press | undefined,
  appear?: Appear,
): { transition?: TransitionProp } => {
  const pressing = press !== undefined && press !== "none";
  if (appear === undefined) {
    return pressing ? { transition: "press" } : {};
  }
  return { transition: appearTransition(appear, pressing) };
};

export const delayTransition = (
  transition: TransitionProp | undefined,
  ms: number,
): TransitionObject => {
  const base: TransitionObject =
    typeof transition === "string"
      ? { default: transition }
      : Array.isArray(transition)
        ? { default: transition[0], ...transition[1] }
        : { ...transition };
  const delay = (base.delay ?? 0) + ms;
  // The Reanimated driver reads a string per-property entry straight from the
  // config, without the top-level delay; the fade has to carry its own.
  return {
    ...base,
    delay,
    ...(typeof base.opacity === "string"
      ? { opacity: { type: base.opacity, delay } }
      : base.opacity && typeof base.opacity === "object"
        ? {
            opacity: {
              ...base.opacity,
              delay: (base.opacity.delay ?? 0) + ms,
            },
          }
        : {}),
  };
};

/** A rule growing out from its left end on mount, at the pace of a line drawing in. */
export const lineGrow = {
  scaleX: 1,
  enterStyle: { scaleX: 0 },
  transformOrigin: "left center",
  transition: {
    ...(isWeb ? {} : { default: "draw" as const }),
    enter: "draw" as const,
  },
} as const;

/** A sheet rising into place on mount, just behind the page opening around it. */
export const sheetRise = (windowHeight: number) => ({
  opacity: 1,
  enterStyle: { opacity: 0, y: windowHeight * 0.45 },
  transition: delayTransition(
    {
      ...(isWeb ? {} : { default: "overlay" as const }),
      enter: "overlay" as const,
      opacity: "fade" as const,
    },
    80,
  ),
});

/** Enter/exit props for an AnimatePresence child or a mount enter; spread onto any element. */
export const appear = (
  shape: Appear,
  { delay = 0 }: { delay?: number } = {},
) => {
  const transition = appearTransition(shape, false);
  return {
    ...appearVariants[shape],
    transition: delay > 0 ? delayTransition(transition, delay) : transition,
  };
};
